import { ButtonGroup, IconButton } from "@components/button";
import { DownIcon, UpIcon } from "@components/icon";
import { IInputOnChangeEvent } from "@components/inputs/input";
import WriteLine from "@components/inputs/writeLine";
import { debounce } from "lodash";
import React from "react";
import styled from "styled-components";

import { INPUT_DEBOUNCE_TIME } from "../../constants";
import { BasicInputSizes, IconSize, TextAlign } from "../../enums";
import { T_PLAIN_big } from "../../global.style";
import i18n from "../../i18n";
import { KeyName } from "../../keyName";
import { ResultCounter, StyledVirtualizedFormSearch } from "./VirtualizedForm.styles";

interface IProps {
    value: string;
    onChange: (value: string) => void;
    activeResult?: number;
    results?: number;
    onHighlightResult?: (idx: number) => void;
    inputId?: string;
    inputRef?: React.Ref<HTMLInputElement>;
}

interface IState {
    searchTerm: string;
}

const WriteLineStyled = styled(WriteLine)`
    ${T_PLAIN_big};
`;

/**
 * Pure component to render a search input e.g. for virtualized forms
 */
export default class VirtualizedFormSearch extends React.Component<IProps, IState> {
    private _lastPropagatedValue: string | undefined;

    constructor(props: IProps) {
        super(props);

        this.state = {
            searchTerm: props.value ?? ""
        };
    }

    componentDidUpdate(prevProps: IProps): void {
        if (prevProps.value !== this.props.value && this.props.value !== this._lastPropagatedValue) {
            this.setState({ searchTerm: this.props.value });
        }
    }

    propagateChange = debounce((value: string) => {
        this._lastPropagatedValue = value;
        this.props.onChange(value);
    }, INPUT_DEBOUNCE_TIME);

    handleChange = (args: IInputOnChangeEvent<string>): void => {
        this.setState({ searchTerm: args.value });
        this.propagateChange(args.value);
        if (args.triggerAdditionalTasks) {
            // trigger immediate change propagation if the change was triggered by
            // a user action (e.g. pressing Enter, clearing field, etc.)
            this.propagateChange.flush();
        }
    };

    get currentResult(): number {
        return this.props.activeResult ?? 0;
    }

    handlePrevious = (): void => {
        let prev = this.currentResult - 1;
        if (prev < 1) {
            prev = this.props.results ?? 0;
        }
        this.props.onHighlightResult?.(prev);
    }

    handleNext = (): void => {
        let next = this.currentResult + 1;
        if (next > (this.props.results ?? 0)) {
            next = 1;
        }
        this.props.onHighlightResult?.(next);
    }

    handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        switch (true) {
            case event.key === KeyName.ArrowUp:
                this.handlePrevious();
                event.preventDefault();
                break;
            case event.key === KeyName.ArrowDown:
                this.handleNext();
                event.preventDefault();
                break;
        }
    }

    render() {
        const { searchTerm } = this.state;
        const { activeResult, results } = this.props;
        const resultCount = results ?? 0;
        const resultsLength = resultCount.toString().length;
        const activeResultFormatted = (activeResult ?? "0")?.toString().padStart(resultsLength, "0");

        const showResults = !!this._lastPropagatedValue;

        return (
            <StyledVirtualizedFormSearch>
                <WriteLineStyled value={searchTerm}
                                 textAlign={TextAlign.Left}
                                 width={BasicInputSizes.L}
                                 id={this.props.inputId ?? "virtualizedFormSearch"}
                                 withClearButton={true}
                                 isExtending={false}
                                 placeholder={i18n.t("Common:VirtualizedForm.SearchPlaceholder")}
                                 onChange={this.handleChange}
                                 onEnterPress={this.handleNext}
                                 onKeyDown={this.handleKeyDown}
                                 passRef={this.props.inputRef}/>
                {showResults && (
                    <>
                        <ResultCounter>{activeResultFormatted}/{resultCount}</ResultCounter>
                        <ButtonGroup>
                            <IconButton title={i18n.t("Common:VirtualizedForm.HighlightPrev")}
                                        onClick={this.handlePrevious}
                                        isDisabled={!resultCount}
                                        isDecorative
                                        isTransparent>
                                <UpIcon width={IconSize.M}/>
                            </IconButton>
                            <IconButton title={i18n.t("Common:VirtualizedForm.HighlightNext")}
                                        onClick={this.handleNext}
                                        isDisabled={!resultCount}
                                        isDecorative
                                        isTransparent>
                                <DownIcon width={IconSize.M}/>
                            </IconButton>
                        </ButtonGroup>
                    </>
                )}
            </StyledVirtualizedFormSearch>
        );
    }
}
